import React from 'react';
import { Link } from 'react-router-dom';
import { BrandLogo } from '@/components/BrandLogo';

/**
 * Public homepage for crm.verseclinic.com.
 * Meta reviewers / crawlers landing on `/` must see the three legal links
 * (Privacy, Terms, Data Deletion) without a login gate. Signed-in staff are
 * routed to /leads by the Protected wrapper used elsewhere.
 */
export default function HomePage() {
  return (
    <div className="min-h-screen bg-[#F5F5F7] text-[#1D1D1F] flex flex-col" data-testid="homepage">
      <header className="border-b border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-5 flex items-center justify-between flex-wrap gap-3">
          <BrandLogo className="h-10 sm:h-12" testid="home-brand" />
          <nav className="text-xs text-slate-600 space-x-4" data-testid="homepage-nav">
            <Link to="/privacy-policy" className="hover:underline" data-testid="home-link-privacy">Privacy Policy</Link>
            <Link to="/terms-of-service" className="hover:underline" data-testid="home-link-terms">Terms</Link>
            <Link to="/data-deletion" className="hover:underline" data-testid="home-link-data-deletion">Data Deletion</Link>
          </nav>
        </div>
      </header>

      <main className="flex-1 max-w-5xl mx-auto px-6 py-16 w-full">
        <section className="grid lg:grid-cols-2 gap-10 items-start">
          <div>
            <div className="inline-flex items-center text-[11px] uppercase tracking-widest text-slate-500 bg-white border border-slate-200 rounded-full px-3 py-1">Internal clinic CRM</div>
            <h1 className="font-serif text-4xl sm:text-5xl mt-5 leading-tight">
              A calmer way to care for every patient enquiry.
            </h1>
            <p className="mt-5 text-base text-slate-700 leading-relaxed">
              VERSE Clinic CRM is the private platform that VERSE CARE SDN. BHD. staff use to receive and respond to patient enquiries from Facebook, Instagram, Messenger, WhatsApp and Meta Lead Ads &mdash; without the chaos of switching tabs.
            </p>
            <p className="mt-3 text-xs text-slate-500">
              This platform is for authorised clinic personnel only. If you are a patient, please message us on our social channels or visit the clinic directly.
            </p>
            <div className="mt-8 flex items-center gap-3 flex-wrap">
              <Link to="/privacy-policy" className="text-sm text-slate-700 hover:text-[#1D1D1F] underline underline-offset-4" data-testid="home-cta-privacy">
                Read our Privacy Policy
              </Link>
            </div>
          </div>

          <aside className="bg-white border border-slate-200 rounded-xl p-6 shadow-sm" data-testid="home-legal-card">
            <div className="font-serif text-lg">Legal and privacy</div>
            <p className="text-xs text-slate-500 mt-1">
              Public documents governing how VERSE Clinic processes personal data and how you can exercise your rights.
            </p>
            <ul className="mt-5 space-y-3 text-sm">
              <li>
                <Link to="/privacy-policy" className="group flex items-start gap-3" data-testid="home-card-privacy">
                  <span className="text-slate-400 group-hover:text-[#1D1D1F]">01</span>
                  <span>
                    <span className="font-medium block group-hover:underline">Privacy Policy</span>
                    <span className="text-xs text-slate-500">How we handle Meta Platform Data, health information and your rights.</span>
                  </span>
                </Link>
              </li>
              <li>
                <Link to="/terms-of-service" className="group flex items-start gap-3" data-testid="home-card-terms">
                  <span className="text-slate-400 group-hover:text-[#1D1D1F]">02</span>
                  <span>
                    <span className="font-medium block group-hover:underline">Terms of Service</span>
                    <span className="text-xs text-slate-500">Rules of acceptable use when messaging our channels or submitting Lead Ad forms.</span>
                  </span>
                </Link>
              </li>
              <li>
                <Link to="/data-deletion" className="group flex items-start gap-3" data-testid="home-card-data-deletion">
                  <span className="text-slate-400 group-hover:text-[#1D1D1F]">03</span>
                  <span>
                    <span className="font-medium block group-hover:underline">Data Deletion Instructions</span>
                    <span className="text-xs text-slate-500">How to request removal of your enquiries, messages and advertising attribution data.</span>
                  </span>
                </Link>
              </li>
            </ul>
            <div className="mt-6 pt-4 border-t border-slate-200 text-[11px] text-slate-500">
              Also available: <Link to="/privacy-policy-bm" className="underline" data-testid="home-link-privacy-bm">Dasar Privasi (Bahasa Malaysia)</Link>
            </div>
          </aside>
        </section>
      </main>

      <footer className="border-t border-slate-200 bg-white">
        <div className="max-w-5xl mx-auto px-6 py-6 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 text-xs text-slate-500">
          <div>
            VERSE CARE SDN. BHD. (Company No. 1663706-A) &middot; Damansara City, Kuala Lumpur, Malaysia
          </div>
          <div className="space-x-4">
            <Link to="/privacy-policy" className="hover:underline">Privacy</Link>
            <Link to="/terms-of-service" className="hover:underline">Terms</Link>
            <Link to="/data-deletion" className="hover:underline">Data Deletion</Link>
            <a href="mailto:admin@verseclinic.com" className="hover:underline">admin@verseclinic.com</a>
          </div>
        </div>
      </footer>
    </div>
  );
}
